import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from "lucide-react";
import Button from "../../components/UI/Button";
import { passwordSchema } from "../../schemavalidations";
import type { PasswordFormData } from "../../types/index";
import { useTranslation } from "react-i18next";
import { translateValidation } from "../../utils/translateValidation";

interface PasswordFormProps {
  onSubmit: (data: PasswordFormData) => Promise<void>;
  loading: boolean;
  title: string;
  description: string;
  submitButtonText: string;
  successMessage?: string;
}

export function PasswordForm({
  onSubmit,
  loading,
  title,
  description,
  submitButtonText,
}: PasswordFormProps) {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
  });

  const password = watch("password");

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    const labels = [
      t("passwordForm.veryWeak"),
      t("passwordForm.weak"),
      t("passwordForm.fair"),
      t("passwordForm.good"),
      t("passwordForm.strong"),
    ];
    const colors = [
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-blue-500",
      "bg-green-500",
    ];

    return {
      strength,
      label: labels[strength - 1] || "",
      color: colors[strength - 1] || "bg-gray-300",
    };
  };

  const passwordStrength = getPasswordStrength(password || "");

  const passwordRequirements = [
    {
      test: (pwd: string) => pwd.length >= 8,
      label: t("passwordForm.atLeast8Chars"),
    },
    {
      test: (pwd: string) => /[a-z]/.test(pwd),
      label: t("passwordForm.oneLowercase"),
    },
    {
      test: (pwd: string) => /[A-Z]/.test(pwd),
      label: t("passwordForm.oneUppercase"),
    },
    {
      test: (pwd: string) => /\d/.test(pwd),
      label: t("passwordForm.oneNumber"),
    },
    {
      test: (pwd: string) => /[@$!%*?&]/.test(pwd),
      label: t("passwordForm.oneSpecialChar"),
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] overflow-hidden shadow-sm">
      <div className="p-8 border-b border-[rgba(11,15,14,0.06)]">
        <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-2">
          {title}
        </h2>
        <p className="text-[#8A9590]">{description}</p>
      </div>

      <form className="p-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-[#8A9590] mb-2">
            {t("passwordForm.newPassword")}
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8A9590]" />
            <input
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder={t("passwordForm.newPasswordPlaceholder")}
              className="w-full pl-12 pr-12 py-3.5 border border-[rgba(11,15,14,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-transparent transition-all duration-200 bg-[#FFFFFF] focus:bg-white text-[#0B0F0E] placeholder:text-[#8A9590]"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-[#F5F4F0] transition-colors"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-[#8A9590]" />
              ) : (
                <Eye className="h-5 w-5 text-[#8A9590]" />
              )}
            </button>
          </div>

          {/* Password Strength Indicator */}
          {password && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#8A9590]">
                  {t("passwordForm.passwordStrength")}
                </span>
                <span
                  className={`text-xs font-medium ${
                    passwordStrength.strength >= 4
                      ? "text-[#00A862]"
                      : passwordStrength.strength >= 3
                        ? "text-[#C97A3C]"
                        : passwordStrength.strength >= 2
                          ? "text-[#F4B23C]"
                          : "text-[#E24B4A]"
                  }`}
                >
                  {passwordStrength.label}
                </span>
              </div>
              <div className="w-full bg-[#F5F4F0] rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                  style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                />
              </div>
            </div>
          )}

          {errors.password && (
            <p className="mt-2 text-sm text-[#E24B4A] flex items-center">
              <span className="w-1 h-1 bg-[#E24B4A] rounded-full mr-2"></span>
              {translateValidation(errors.password.message || "")}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-[#8A9590] mb-2">
            {t("passwordForm.confirmPassword")}
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8A9590]" />
            <input
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("passwordForm.confirmPasswordPlaceholder")}
              className="w-full pl-12 pr-12 py-3.5 border border-[rgba(11,15,14,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-transparent transition-all duration-200 bg-[#FFFFFF] focus:bg-white text-[#0B0F0E] placeholder:text-[#8A9590]"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-[#F5F4F0] transition-colors"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5 text-[#8A9590]" />
              ) : (
                <Eye className="h-5 w-5 text-[#8A9590]" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-2 text-sm text-[#E24B4A] flex items-center">
              <span className="w-1 h-1 bg-[#E24B4A] rounded-full mr-2"></span>
              {translateValidation(errors.confirmPassword.message || "")}
            </p>
          )}
        </div>

        {/* Password Requirements */}
        {password && (
          <div className="bg-[#F5F4F0] rounded-lg p-4 border border-[rgba(11,15,14,0.06)]">
            <h4 className="text-xs font-medium uppercase tracking-widest text-[#8A9590] mb-3">
              {t("passwordForm.passwordRequirements")}
            </h4>
            <div className="space-y-2">
              {passwordRequirements.map((req, index) => {
                const isValid = req.test(password);
                return (
                  <div key={index} className="flex items-center">
                    {isValid ? (
                      <CheckCircle className="h-4 w-4 text-[#00A862] mr-2" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-[#8A9590] mr-2" />
                    )}
                    <span
                      className={`text-xs ${
                        isValid ? "text-[#00A862]" : "text-[#8A9590]"
                      }`}
                    >
                      {req.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3.5 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium shadow-sm"
          isLoading={loading}
          disabled={loading}
        >
          {loading ? t("passwordForm.processing") : submitButtonText}
        </Button>
      </form>
    </div>
  );
}
